import { components, MultiValueGenericProps } from 'react-select';
import { useFormikContext } from 'formik';
import { useState } from 'react';

import { TimeWindowPickerInputGroup } from '@/react/portainer/environments/common/TimeWindowPicker/TimeWindowPickerInputGroup';

import { FormSectionTitle } from '@@/form-components/FormSectionTitle';
import { Input } from '@@/form-components/Input';
import { SwitchField } from '@@/form-components/SwitchField';
import { TextTip } from '@@/Tip/TextTip';
import { InputList, ItemProps } from '@@/form-components/InputList';
import { Select } from '@@/form-components/ReactSelect';

import {
  availableAccessModes,
  AccessModeOption,
  DEFAULT_ACCESS_MODE,
  StorageClass,
  IngressController,
  KubernetesSetupFormValues,
} from './types';

/**
 * Custom MultiValueLabel to show short access mode names (RWO, RWX)
 * instead of full descriptions in the selected items
 */
function AccessModeMultiValueLabel({
  data,
  innerProps,
  selectProps,
}: MultiValueGenericProps<AccessModeOption>) {
  return (
    <components.MultiValueLabel
      data={data}
      innerProps={innerProps}
      selectProps={selectProps}
    >
      {data.Name}
    </components.MultiValueLabel>
  );
}

/**
 * Kubernetes Setup Policy form fields.
 * Pure field renderer without FormActions - used by both create and edit modes.
 */
export function KubernetesSetupFormFields() {
  const { values, setFieldValue } =
    useFormikContext<KubernetesSetupFormValues>();
  // Timezone state for change window configuration
  const [timeZone, setTimeZone] = useState('UTC');

  // Ensure ingressControllers is always an array
  const ingressControllers = Array.isArray(values.ingressControllers)
    ? values.ingressControllers
    : [];

  // Ensure storageClasses is always an array
  const storageClasses = Array.isArray(values.storageClasses)
    ? values.storageClasses
    : [];

  return (
    <>
      {/* Networking - Services */}
      <FormSectionTitle>Networking - Services</FormSectionTitle>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="blue" inline={false}>
            Enabling the load balancer feature will allow users to expose
            applications they deploy over an external IP address assigned by the
            cloud provider.
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="orange" inline={false}>
            If you want to use this feature, ensure your cloud provider allows
            you to create load balancers. This may incur costs.
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <SwitchField
            label="Allow users to use external load balancers"
            tooltip="Allow users to use external load balancers"
            labelClass="col-sm-5 col-lg-4"
            checked={values.useLoadBalancer || false}
            onChange={(value) => setFieldValue('useLoadBalancer', value)}
            data-cy="load-balancer-toggle"
          />
        </div>
      </div>

      {/* Networking - Ingresses */}
      <FormSectionTitle>Networking - Ingresses</FormSectionTitle>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="blue" inline={false}>
            <p>
              Enabling ingress controllers in your cluster allows them to be
              available in the Portainer UI for users to publish applications
              over HTTP/HTTPS. A controller must have a class name for it to be
              included here.
            </p>
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <InputList
            label="Allowed Ingress Controllers"
            value={ingressControllers}
            onChange={(value) => setFieldValue('ingressControllers', value)}
            item={IngressControllerItem}
            itemBuilder={() => ({ name: '' })}
            addLabel="Add ingress controller"
            tooltip="The list of ingress controllers defined must be pre-installed in the cluster."
            data-cy="ingress-controllers-list"
          />
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <SwitchField
            label='Allow ingress class to be set to "none"'
            tooltip='This allows users setting up ingresses to select "none" as the ingress class.'
            labelClass="col-sm-5 col-lg-4"
            checked={values.allowNoneIngressClass || false}
            onChange={(value) => setFieldValue('allowNoneIngressClass', value)}
            data-cy="allow-none-ingress-class-toggle"
          />
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <SwitchField
            label="Configure ingress controller availability per namespace"
            tooltip="This allows an administrator to configure, in each namespace, which ingress controllers will be available for users to select when setting up ingresses for applications."
            labelClass="col-sm-5 col-lg-4"
            checked={values.ingressAvailabilityPerNamespace || false}
            onChange={(value) =>
              setFieldValue('ingressAvailabilityPerNamespace', value)
            }
            data-cy="ingress-availability-per-namespace-toggle"
          />
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <SwitchField
            label="Only allow admins to deploy ingresses"
            tooltip="Enforces only allowing admins to deploy ingresses (and disallows standard users from doing so)."
            labelClass="col-sm-5 col-lg-4"
            checked={values.restrictStandardUserIngressW || false}
            onChange={(value) =>
              setFieldValue('restrictStandardUserIngressW', value)
            }
            data-cy="restrict-standard-user-ingress-toggle"
          />
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="blue" inline={false}>
            <p>
              You may set up ingress defaults (hostnames and annotations) via
              Create/Edit ingress. Users may then select them via the hostname
              dropdown in Create/Edit application.
            </p>
          </TextTip>
        </div>
      </div>

      {/* Change Window Settings */}
      <FormSectionTitle>Change Window Settings</FormSectionTitle>

      <div className="form-group">
        <div className="col-sm-12">
          <SwitchField
            label="Enable Change Window"
            tooltip="GitOps updates to stacks or applications outside the defined change window will not occur."
            labelClass="col-sm-5 col-lg-4"
            checked={values.changeWindow?.Enabled || false}
            onChange={(value) =>
              setFieldValue('changeWindow', {
                ...(values.changeWindow || {
                  Enabled: false,
                  StartTime: '22:00',
                  EndTime: '02:00',
                }),
                Enabled: value,
              })
            }
            data-cy="change-window-toggle"
          />
        </div>
      </div>

      {values.changeWindow?.Enabled && (
        <>
          <div className="form-group">
            <div className="col-sm-12">
              <TimeWindowPickerInputGroup
                values={values.changeWindow}
                onChangeTimeZone={setTimeZone}
                onChangeChangeWindow={(changeWindow) =>
                  setFieldValue('changeWindow', changeWindow)
                }
                timeZone={timeZone}
              />
            </div>
          </div>
          <div className="form-group">
            <div className="col-sm-12">
              <span className="text-warning small flex gap-1 align-top">
                <div className="icon icon-sm">
                  <i className="fa fa-exclamation-triangle" />
                </div>
                <div>
                  GitOps updates to stacks or applications outside the defined
                  change window will not occur.
                </div>
              </span>
            </div>
          </div>
        </>
      )}

      {/* Resources and Metrics */}
      <FormSectionTitle>Resources and Metrics</FormSectionTitle>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="blue" inline={false}>
            <p>
              By DISABLING resource over-commit (highly recommended), you can
              ONLY assign namespaces CPU and memory resources that are less (in
              aggregate) than the cluster total minus any system resource
              reservation.
            </p>
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="orange" inline={false}>
            <p>
              By ENABLING resource over-commit, you can assign namespaces more
              resources than are physically available in the cluster. This may
              lead to unexpected deployment failures if there are insufficient
              resources to service demand.
            </p>
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <SwitchField
            label="Allow resource over-commit"
            tooltip="Allow resource over-commit"
            labelClass="col-sm-5 col-lg-4"
            checked={values.enableResourceOverCommit || false}
            onChange={(value) =>
              setFieldValue('enableResourceOverCommit', value)
            }
            data-cy="resource-over-commit-toggle"
          />
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="blue" inline={false}>
            <p>
              Enabling the metrics feature allows users to use horizontal pod
              autoscaling and to see container and node resource usage. This
              requires metrics server or prometheus to be running in your
              cluster.
            </p>
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip inline={false}>
            <p>
              On any subsequent disabling of the feature, existing deployed
              applications with autoscaling will still autoscale (you would have
              to remove their autoscaler definitions to stop this).
            </p>
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <SwitchField
            label="Enable features using the metrics API"
            tooltip="Enable features using the metrics API"
            labelClass="col-sm-5 col-lg-4"
            checked={values.useServerMetrics || false}
            onChange={(value) => setFieldValue('useServerMetrics', value)}
            data-cy="server-metrics-toggle"
          />
        </div>
      </div>

      {/* Available storage options */}
      <FormSectionTitle>Available storage options</FormSectionTitle>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip color="blue" inline={false}>
            <p>
              Select which storage options will be available for use when
              deploying applications. Have a look at your storage driver
              documentation to figure out which access policy to configure and
              if the volume expansion capability is supported.
            </p>
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <TextTip inline={false}>
            <p>
              You can find more information about access modes in the{' '}
              <a
                href="https://kubernetes.io/docs/concepts/storage/persistent-volumes/#access-modes"
                target="_blank"
                rel="noopener noreferrer"
              >
                official Kubernetes documentation
              </a>
              .
            </p>
          </TextTip>
        </div>
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <InputList
            label="Storage Classes"
            value={storageClasses}
            onChange={(value) => setFieldValue('storageClasses', value)}
            item={StorageClassItem}
            itemBuilder={() => ({
              name: '',
              accessModes: [DEFAULT_ACCESS_MODE],
              allowVolumeExpansion: false,
            })}
            addLabel="Add storage class"
            tooltip="Define storage classes and configure volume expansion for each"
            data-cy="storage-classes-list"
          />
        </div>
      </div>
    </>
  );
}

function IngressControllerItem({
  item,
  onChange,
  index,
}: ItemProps<IngressController>) {
  return (
    <div className="flex items-center">
      <div className="max-w-xs">
        <Input
          type="text"
          value={item.name}
          onChange={(e) => onChange({ ...item, name: e.target.value })}
          placeholder="Ingress controller name"
          data-cy={`ingress-controller-name-${index}`}
        />
      </div>
    </div>
  );
}

function StorageClassItem({ item, onChange, index }: ItemProps<StorageClass>) {
  // Convert string access modes to AccessModeOption objects for the Select
  const selectedAccessModes = (item.accessModes || [])
    .map((mode) => availableAccessModes.find((am) => am.Name === mode))
    .filter((am): am is AccessModeOption => am !== undefined);

  return (
    <div className="flex items-center gap-4">
      <div className="w-48">
        <Input
          type="text"
          value={item.name}
          onChange={(e) => onChange({ ...item, name: e.target.value })}
          placeholder="Storage class name"
          data-cy={`storage-class-name-${index}`}
        />
      </div>
      <div className="w-64">
        <Select
          isMulti
          options={availableAccessModes}
          value={selectedAccessModes}
          getOptionLabel={(option) => option.Description}
          getOptionValue={(option) => option.Name}
          components={{ MultiValueLabel: AccessModeMultiValueLabel }}
          onChange={(selected) =>
            onChange({
              ...item,
              accessModes: selected.map((s) => s.Name),
            })
          }
          placeholder="Select access modes"
          closeMenuOnSelect={false}
          data-cy={`storage-class-access-modes-${index}`}
        />
      </div>
      <div className="shrink-0">
        <SwitchField
          label="Allow volume expansion"
          labelClass="!w-auto"
          checked={item.allowVolumeExpansion}
          onChange={(value) =>
            onChange({ ...item, allowVolumeExpansion: value })
          }
          data-cy={`storage-class-expansion-${index}`}
        />
      </div>
    </div>
  );
}
